import { useState } from 'react';
import { Mail, KeyRound, Github, Globe, CheckCircle2 } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { TopNav, Footer } from '../components/Chrome';

function AuthShell({ navigate, children }) {
  return (
    <div className="auth-wrap">
      <TopNav navigate={navigate} variant="minimal" />
      <div className="auth-body">
        <div className="auth-card fade-up">
          <div className="panel panel-pad">{children}</div>
        </div>
      </div>
      <Footer navigate={navigate} />
    </div>
  );
}

function OAuthButtons({ onOAuth, disabled }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
      <button className="btn btn-outline btn-block" disabled={disabled} onClick={() => onOAuth('Google')}>
        <Globe size={16} /> Continue with Google
      </button>
      <button className="btn btn-outline btn-block" disabled={disabled} onClick={() => onOAuth('GitHub')}>
        <Github size={16} /> Continue with GitHub
      </button>
    </div>
  );
}

export function LoginPage({ navigate, handleLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      const { data: profile } = await supabase
        .from('user_profiles').select('*').eq('user_id', data.user.id).single();
      if (profile) {
        handleLogin({
          id: data.user.id,
          email: data.user.email,
          name: profile.name,
          country: profile.country,
          city: profile.city,
          subscription: profile.subscription,
          joinDate: profile.created_at,
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOAuth = async (provider) => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: provider.toLowerCase(),
        options: { redirectTo: `${window.location.origin}/dashboard` },
      });
      if (error) throw error;
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <AuthShell navigate={navigate}>
      <h2>Welcome back</h2>
      <p className="sub">Sign in to access your dashboard.</p>
      {error && <div className="alert alert-error">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="field">
          <label>Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com" required disabled={loading} />
        </div>
        <div className="field">
          <label>Password</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••" required disabled={loading} />
        </div>
        <div style={{ textAlign: 'right', marginBottom: '1.1rem' }}>
          <button type="button" className="linklike" style={{ fontSize: '0.82rem' }}
            onClick={() => navigate('/forgot-password')}>
            Forgot password?
          </button>
        </div>
        <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <div className="divider">OR</div>
      <OAuthButtons onOAuth={handleOAuth} />
      <p className="auth-foot">
        No account?{' '}
        <button className="linklike" onClick={() => navigate('/signup')}>Sign up</button>
      </p>
    </AuthShell>
  );
}

export function SignupPage({ navigate, handleLogin }) {
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', confirmPassword: '', country: '', city: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setFormData({ ...formData, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match'); return; }
    if (formData.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: { data: { name: formData.name, country: formData.country, city: formData.city } },
      });
      if (authError) throw authError;

      if (authData.user && !authData.session) {
        setError('Account created! Please check your email (including spam folder) to verify your account before logging in.');
        setLoading(false);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      if (authData.session) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: profile } = await supabase
          .from('user_profiles').select('*').eq('user_id', authData.user.id).single();
        handleLogin({
          id: authData.user.id,
          email: authData.user.email,
          name: profile?.name ?? formData.name,
          country: profile?.country ?? formData.country,
          city: profile?.city ?? formData.city,
          subscription: profile?.subscription ?? 'free',
          joinDate: profile?.created_at ?? new Date().toISOString(),
        });
      }
    } catch (err) {
      setError(err.message || 'An error occurred during signup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOAuth = async (provider) => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: provider.toLowerCase(),
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
          data: { name: 'OAuth User', country: 'Unknown', city: 'Unknown' },
        },
      });
      if (error) throw error;
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <AuthShell navigate={navigate}>
      <h2>Create account</h2>
      <p className="sub">Start with the free tier — no card required.</p>
      {error && (
        <div className={error.includes('created!') ? 'alert alert-ok' : 'alert alert-error'}>{error}</div>
      )}
      <form onSubmit={handleSubmit}>
        <div className="field">
          <label>Full name</label>
          <input value={formData.name} onChange={set('name')} placeholder="Jane Doe" required disabled={loading} />
        </div>
        <div className="field">
          <label>Email</label>
          <input type="email" value={formData.email} onChange={set('email')} placeholder="you@company.com" required disabled={loading} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }} className="field">
          <div>
            <label>Country</label>
            <input value={formData.country} onChange={set('country')} placeholder="India" required disabled={loading} />
          </div>
          <div>
            <label>City</label>
            <input value={formData.city} onChange={set('city')} placeholder="Mumbai" required disabled={loading} />
          </div>
        </div>
        <div className="field">
          <label>Password</label>
          <input type="password" value={formData.password} onChange={set('password')} placeholder="••••••••" required disabled={loading} />
        </div>
        <div className="field">
          <label>Confirm password</label>
          <input type="password" value={formData.confirmPassword} onChange={set('confirmPassword')} placeholder="••••••••" required disabled={loading} />
        </div>
        <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
          {loading ? 'Creating account…' : 'Create account'}
        </button>
      </form>
      <div className="divider">OR</div>
      <OAuthButtons onOAuth={handleOAuth} disabled={loading} />
      <p className="auth-foot">
        Already have an account?{' '}
        <button className="linklike" onClick={() => navigate('/login')}>Sign in</button>
      </p>
    </AuthShell>
  );
}

export function ForgotPasswordPage({ navigate }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell navigate={navigate}>
      <div style={{ marginBottom: '1rem' }}><KeyRound size={28} color="var(--accent)" /></div>
      <h2>Reset password</h2>
      <p className="sub">Enter your email to receive reset instructions.</p>
      {error && <div className="alert alert-error">{error}</div>}
      {!sent ? (
        <form onSubmit={handleSubmit}>
          <div className="field">
            <label>Email address</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com" required disabled={loading} />
          </div>
          <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
            {loading ? 'Sending…' : 'Send reset link'}
          </button>
        </form>
      ) : (
        <div className="alert alert-ok" style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
          <CheckCircle2 size={18} /> Email sent — check your inbox for instructions.
        </div>
      )}
      <p className="auth-foot">
        <button className="linklike" onClick={() => navigate('/login')}>Back to login</button>
      </p>
    </AuthShell>
  );
}

export function ForgotUsernamePage({ navigate }) {
  return (
    <AuthShell navigate={navigate}>
      <div style={{ marginBottom: '1rem' }}><Mail size={28} color="var(--accent)" /></div>
      <h2>Recover username</h2>
      <p className="sub">Your username is your email address. Please use the login page.</p>
      <button className="btn btn-primary btn-block" onClick={() => navigate('/login')}>
        Back to login
      </button>
    </AuthShell>
  );
}
